import React from 'react';
import { FileUpload } from '@emorg-prd/standard-react';
import DoNotDecryptRule from '../../../../models/entities/DoNotDecryptRule';				
import { DoNotDecryptRequestsDirection } from '../../../../models/enums/DNDR/DoNotDecryptRequestsDirection';
import DoNotDecryptRequestService from '../../../../services/DoNotDecryptRequestService';				
import CSVGeneratorButton from '../../../common/CSVGeneratorButton';
import { RuleColumnsLayout, DNDRColumn } from '../RuleColumnsLayout';
import StandardModal from '../../../common/modals/StandardModal';
import { Button, ButtonGroup, Icon } from '../../../common';
import { Alert } from '../../../common';

interface BulkUploadModalProps {
	direction: DoNotDecryptRequestsDirection;
	onSuccess: (rules: DoNotDecryptRule[]) => any;
	onCancel: (...args: any[]) => any;
	visible: boolean;
	subscriptionRequestId: number;
}

interface BulkUploadModalState {
	loading: boolean;
	file: any;
	fileKey: number;
	alert: {
		visible: boolean;
		text?: string;
	},
	templateColumns: DNDRColumn[];
}

export default class BulkUploadModal
	extends React.Component<BulkUploadModalProps, BulkUploadModalState> {
	
	doNotDecryptRequestService: DoNotDecryptRequestService;				

	constructor(props: BulkUploadModalProps) {
		super(props);

		this.doNotDecryptRequestService = new DoNotDecryptRequestService();				

		this.state = {
			loading: false,
			file: null,
			fileKey: 0,
			alert: {
				visible: false,
			},
			templateColumns: [],
		};
	}

	componentDidUpdate(prevProps) {
		if (prevProps.direction?.value !== this.props.direction?.value) {
			this.setColumns();
		}
	}

	handleChange = (file) => {
		this.setState({
			file: file[0],
			alert: {
				visible: false,
			}
		});
	};

	handleUpload = async () => {
		this.setState({ loading: true });
		this.doNotDecryptRequestService.parse(this.props.direction, this.state.file, Number(this.props.subscriptionRequestId))
			.then((response: DoNotDecryptRule[]) => {
				const rules = response.map(r => new DoNotDecryptRule(r));				
				this.props.onSuccess(rules);
			})
			.catch(err => {
				this.handleFileRemoval();
				this.setState({
					alert: {
						visible: true,
						text: err.message
					}
				});
			})
			.finally(() => {
				this.setState({ loading: false });
			});
	};

	handleFileRemoval = () => {
		this.setState((prev) => ({
			file: null,
			fileKey: prev.fileKey + 1,
			alert: { visible: false },
		}));
	};

	setColumns = () => {
		this.setState({
			templateColumns: RuleColumnsLayout.get(this.props.direction)
		});
	};

	render() {
		return (
			<StandardModal
				show={this.props.visible}
				title="Import rules to the request"
				onDismiss={this.props.onCancel as () => any}
				footer={
					<ButtonGroup>
						<Button
							variant="primary"
							onClick={this.handleUpload}
							isLoading={this.state.loading}
							disabled={(!this.state.file) || this.state.alert.visible}
						>
							Upload Rules
						</Button>
						<Button
							variant="secondary"
							onClick={this.handleFileRemoval}
							isLoading={this.state.loading}
							disabled={(!this.state.file) || this.state.alert.visible}
						>
							<Icon icon='trashcan' size='small' className={"em-u-margin-right-half"}></Icon>
							Remove File
						</Button>
					</ButtonGroup>
				}
			>
				<CSVGeneratorButton
					label="Download Template"
					columns={
						this.props.direction == DoNotDecryptRequestsDirection.SubscriptionToInternet ?
						[
							{ header: "Operation", cell: (DNDRColumn) => DNDRColumn.operation },
							{ header: "Name", cell: (DNDRColumn) => DNDRColumn.operation },
							{ header: "Action", cell: (DNDRColumn) => DNDRColumn.operation },
							{ header: "Region", cell: (DNDRColumn) => DNDRColumn.operation },
							{ header: "Source IPs", cell: (DNDRColumn) => DNDRColumn.operation },
							{ header: "Destination URLs", cell: (DNDRColumn) => DNDRColumn.operation },
							{ header: "Description", cell: (DNDRColumn) => DNDRColumn.operation },
						]
						:
						[
							{ header: "Operation", cell: (DNDRColumn) => DNDRColumn.operation },
							{ header: "Name", cell: (DNDRColumn) => DNDRColumn.operation },
							{ header: "Action", cell: (DNDRColumn) => DNDRColumn.operation },
							{ header: "Region", cell: (DNDRColumn) => DNDRColumn.operation },
							{ header: "Traffic Direction", cell: (DNDRColumn) => DNDRColumn.operation },
							{ header: "Source IPs", cell: (DNDRColumn) => DNDRColumn.operation },
							{ header: "Destination URLs", cell: (DNDRColumn) => DNDRColumn.operation },
							{ header: "Destination IPs", cell: (DNDRColumn) => DNDRColumn.operation },
							{ header: "Description", cell: (DNDRColumn) => DNDRColumn.operation },
						]
					}
					fileName={`Template-Rule-${this.props.direction?.name}.csv`}
					data={[]}
				/>
				<FileUpload
					key={this.state.fileKey}
					multiple={false}
					onChange={this.handleChange}
				/>
				{this.state.alert.visible &&
					<Alert
						variant="error"
						body={this.state.alert.text}
						onClose={() => {
							this.setState({ alert: { visible: false } });
						}}
					>
						{this.state.alert.text}
					</Alert>}
			</StandardModal>
		);
	}
}

interface BulkUploadModalProps {
	direction: DoNotDecryptRequestsDirection;
	onSuccess: (rules: DoNotDecryptRule[]) => any;
	onCancel: (...args: any[]) => any;
	visible: boolean;
	subscriptionRequestId: number;
}

interface BulkUploadModalState {
	loading: boolean;
	file: any;
	alert: {
		visible: boolean;
		text?: string;
	},
	templateColumns: DNDRColumn[];
}